import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as Yup from 'yup';
import { CourseLevel, DayOfTheWeek, Field } from './courseData';
dayjs.extend(customParseFormat);

export const courseDataValidationSchema = Yup.object().shape({
  // trim in strict mode will validate no whitespace instead of transform
  name: Yup.string()
    .required('Required.')
    .trim('Name cannot include leading and trailing spaces')
    .strict(true)
    .min(2, 'Name should be at least 2 characters')
    .max(75, 'Name should be at most 75 characters.'),
  field: Yup.string().oneOf(Object.values(Field)),
  description: Yup.string()
    .required('Required.')
    .trim('Description cannot include leading and trailing spaces')
    .strict(true)
    .min(20, 'Description should be at least 20 characters')
    .max(300, 'Description should be at most 300 characters.'),
  prereqNotes: Yup.string()
    .trim('Prerequisite Notes cannot include leading and trailing spaces')
    .strict(true)
    .max(200, 'Prerequisite Notes should be at most 200 characters.'),
  zoomLink: Yup.string()
    .required('Required')
    .matches(
      /https?:\/\/([A-Za-z0-9-_.!~*'()]+\.)?zoom\.us\/?.*/,
      "That doesn't look like a valid Zoom link. If it is, please contact tech@youthpassionproject.org."
    ),
  courseLevel: Yup.string().oneOf(Object.values(CourseLevel)),
  studentCountInfo: Yup.object().shape({
    recommended: Yup.number()
      .typeError('Must be a number.')
      .required('Required.')
      .min(1, 'Recommended student count should be at least 1.')
      .max(20, 'Recommended student count should be at most 20.')
      .integer('Should be an integer.')
      .lessThan(Yup.ref('max'), 'Should be less than max student count.'),
    max: Yup.number()
      .typeError('Must be a number.')
      .required('Required.')
      .min(0, 'Max student count should be at least 1.')
      .max(20, 'Max student count should be less than 20.')
      .integer('Should be an integer.')
      .moreThan(Yup.ref('recommended'), 'Should be more than recommended student count.'),
  }),
  gradeInfo: Yup.object().shape({
    regular: Yup.object().shape({
      min: Yup.number()
        .typeError('Must be a number.')
        .required('Required.')
        .min(1, 'Should be at least 1.')
        .max(8, 'Should be at most 8.')
        .integer('Should be an integer.')
        .lessThan(Yup.ref('max'), 'Should be less than max grade.')
        .test('is-3-away-from-max', 'Should be at most 3 away from maximum grade.', function (min) {
          const max = this.resolve(Yup.ref('max'));
          if (typeof max === 'number' && min) {
            return max - min >= 1 && max - min <= 3;
          }
          return false;
        }),
      max: Yup.number()
        .typeError('Must be a number')
        .required('Required.')
        .min(1, 'Should be at least 1.')
        .max(8, 'Should be at most 8.')
        .integer('Should be an integer.')
        .moreThan(Yup.ref('min'), 'Should be more than min.'),
    }),
    exception: Yup.object().shape({
      min: Yup.number()
        .typeError('Must be a number.')
        .required('Required.')
        .min(1, 'Should be greater than 1.')
        .max(8, 'Should be less than 8.')
        .integer('Should be an integer.')
        .lessThan(Yup.ref('max'), 'Should be less than max grade.')
        .test('is-5-away-from-max', 'Should be at most 5 away from maximum grade.', function (min) {
          const max = this.resolve(Yup.ref('max'));
          if (typeof max === 'number' && min) {
            return max - min >= 1 && max - min <= 5;
          }
          return false;
        }),
      max: Yup.number()
        .typeError('Must be a number.')
        .required('Required.')
        .min(1, 'Should be greater than 1.')
        .max(8, 'Should be less than 8.')
        .integer('Should be an integer.')
        .moreThan(Yup.ref('min'), 'Should be more than min.'),
    }),
  }),
  sections: Yup.array(
    Yup.object().shape({
      meetings: Yup.array(
        Yup.object().shape({
          day: Yup.string().required('Required.').oneOf(Object.values(DayOfTheWeek)),
          time: Yup.mixed().required('Required.'), // Time input guarantees hh:mm
        })
      )
        .test('meetings-on-different-days', 'Meetings must be on different days.', (arr) => {
          const meetings = arr?.map((v) => v.day);
          return new Set(meetings).size === meetings?.length;
        })
        .min(1, 'Each section must have at least one meeting'),
    })
  ).min(1, 'Must have at least one section'),
});
